import { isAxiosError } from 'axios';

import { HttpStatusCode } from '../assets/enums/index.ts';

export const API_ERROR_PAGE_EVENT = 'api:error-page';
export const NETWORK_ERROR_CODE = 'NETWORK_ERROR';

export type ApiErrorPageEventDetail = {
  statusCode?: number | typeof NETWORK_ERROR_CODE;
  message?: string;
};

let apiErrorPageEventDetail: ApiErrorPageEventDetail | null = null;

export const getStoredApiErrorPageEventDetail = () => {
  return apiErrorPageEventDetail;
};

export const clearStoredApiErrorPageEventDetail = () => {
  apiErrorPageEventDetail = null;
};

export const isNotFoundResponseError = (error: unknown) => {
  return isAxiosError(error) && error.response?.status === HttpStatusCode.NOT_FOUND_404;
};

/** Returns true when an API response indicates that the current session is unauthorized. */
export const isUnauthorizedResponseError = (error: unknown) => {
  return isAxiosError(error) && error.response?.status === HttpStatusCode.UNAUTHORIZED_401;
};

export const isServerResponseError = (error: unknown) => {
  return (
    isAxiosError(error) && (error.response?.status ?? 0) >= HttpStatusCode.INTERNAL_SERVER_ERROR_500
  );
};

export const isNetworkResponseError = (error: unknown) => {
  return (
    isAxiosError(error) &&
    !error.response &&
    Boolean(error.request) &&
    error.code !== 'ERR_CANCELED'
  );
};

export const shouldDisplayApiErrorPage = (error: unknown) => {
  return isServerResponseError(error) || isNetworkResponseError(error);
};

export const throwOnNotFoundResponse = (error: unknown) => {
  return isNotFoundResponseError(error);
};

const stringifyErrorValue = (value: unknown) => {
  if (typeof value === 'string') {
    return value;
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const getResponseDataMessage = (data: unknown) => {
  if (data && typeof data === 'object' && 'message' in data) {
    return stringifyErrorValue((data as { message?: unknown }).message);
  }
  if (data && typeof data === 'object' && 'root' in data) {
    return stringifyErrorValue((data as { root?: unknown }).root);
  }

  if (data && typeof data === 'object' && 'response' in data) {
    const response = (data as { response?: unknown }).response;

    if (response && typeof response === 'object' && 'message' in response) {
      return stringifyErrorValue((response as { message?: unknown }).message);
    }
    if (response && typeof response === 'object' && 'root' in response) {
      return stringifyErrorValue((response as { root?: unknown }).root);
    }
  }

  return undefined;
};

export const getApiErrorStatusCode = (error: unknown) => {
  if (isNetworkResponseError(error)) {
    return NETWORK_ERROR_CODE;
  }

  if (isAxiosError(error)) {
    return error.response?.status;
  }

  return undefined;
};

export const getApiErrorMessage = (error: unknown) => {
  if (isAxiosError(error)) {
    const responseDataMessage = getResponseDataMessage(error.response?.data);

    if (responseDataMessage) {
      return responseDataMessage;
    }

    if (error.response?.data) {
      return stringifyErrorValue(error.response.data);
    }

    return stringifyErrorValue(error.toJSON());
  }

  if (error instanceof Error) {
    return error.message;
  }

  return stringifyErrorValue(error);
};

export const getApiErrorPageEventDetail = (error: unknown): ApiErrorPageEventDetail => ({
  statusCode: getApiErrorStatusCode(error),
  message: isNetworkResponseError(error)
    ? 'Network error: Backend did not respond.'
    : getApiErrorMessage(error),
});

export const emitApiErrorPageEvent = (error: unknown) => {
  apiErrorPageEventDetail = getApiErrorPageEventDetail(error);

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent<ApiErrorPageEventDetail>(API_ERROR_PAGE_EVENT, {
        detail: apiErrorPageEventDetail,
      })
    );
  }
};
